<script setup lang="ts">
import { computed, ref, nextTick, watch } from 'vue'
import { onClickOutside, useResizeObserver } from '@vueuse/core'
import { useRuntimeConfig } from '#app'
import { useI18n } from '#imports'

import { LinkSecondary, Button } from 'refresh-ui'
import InstantSearchInput from '../molecules/InstantSearchInput.vue'
import { removeCmsDomain } from 'refresh-common'

const { t } = useI18n()
const config = useRuntimeConfig()

// eslint-disable-next-line no-undef
const searchClient = useAlgoliaRef()

const searchContainer = ref<HTMLElement | null>(null) // Ref to outer container for click detection

const visibleHits = ref(15) // Number of hits to initially show (and load more)
const isDropdownOpen = ref(false) // Whether the dropdown is currently open
const dropdownPosition = ref<'bottom' | 'top'>('bottom') // Direction to show dropdown

// Close dropdown handler
const closeDropdown = () => {
  isDropdownOpen.value = false
}

// Detect outside clicks and close dropdown if clicked outside
onClickOutside(searchContainer, () => closeDropdown())

useResizeObserver(searchContainer, () => {
  updateDropdownPosition()
})

// Decide whether to show the dropdown above or below input depending on available space
const updateDropdownPosition = () => {
  if (!searchContainer.value) return

  const rect = searchContainer.value.getBoundingClientRect()
  const isDesktop = window.innerWidth >= 1024 // lg: 1024px in Tailwind
  const dropdownHeight = isDesktop ? 700 : 400 // estimated dropdown height (should match UI max height)
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top

  // Show above only if there's not enough room below but enough above
  dropdownPosition.value =
    spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? 'top' : 'bottom'
}

// When dropdown is opened, calculate position (top/bottom)
watch(isDropdownOpen, val => {
  if (val) {
    nextTick(() => updateDropdownPosition())
  }
})

// Choose correct Algolia index based on current language
const indexName = computed(() => {
  switch (config.public.siteLang) {
    case 'en':
      return config.public.searchAlgoliaIndexEn
    case 'uk':
      return config.public.searchAlgoliaIndexUk
    default:
      return config.public.searchAlgoliaIndexSv
  }
})
</script>

<template>
  <client-only>
    <ais-instant-search :search-client="searchClient" :index-name="indexName">
      <!-- Outer wrapper to help position and detect clicks -->
      <div ref="searchContainer" class="relative mx-auto w-full">
        <!-- Search input box -->
        <InstantSearchInput @focus="isDropdownOpen = true" />

        <!-- Results container -->
        <div>
          <ais-state-results>
            <template #default="{ results, state, error }">
              <!-- Optional debug output -->
              <pre>{{ error }}</pre>

              <!-- Show dropdown if query exists and we got hits -->
              <div v-if="state.query && results.hits.length">
                <transition name="fade">
                  <div
                    v-show="isDropdownOpen"
                    class="absolute left-0 z-50 max-h-[400px] w-full overflow-y-auto rounded-xl bg-white shadow-xl lg:max-h-[700px]"
                    :class="dropdownPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'"
                  >
                    <ais-hits>
                      <template #default="{ items }">
                        <!-- Results header -->
                        <div
                          class="items-left sticky top-0 z-10 mb-0 mt-1 flex flex-col bg-white pl-1 pt-1 text-sm"
                        >
                          <div>
                            {{ Math.min(visibleHits, items.length) }} / {{ items.length }}
                            {{ $t('results') }}
                          </div>
                        </div>

                        <!-- List of result items -->
                        <div class="flex flex-col gap-4 p-2">
                          <NuxtLink
                            v-for="item in items.slice(0, visibleHits)"
                            :key="item.objectID"
                            :to="removeCmsDomain(item.url)"
                            class="flex w-full flex-col gap-4 rounded-lg border border-gray-200 p-4 transition hover:shadow-md"
                          >
                            <!-- Item text content -->
                            <div class="flex flex-1 flex-col justify-between">
                              <h3 class="mb-2 text-base font-bold leading-tight text-black">
                                <span
                                  class="highlighted-text"
                                  v-html="item._highlightResult?.title?.value || item.title"
                                />
                              </h3>
                              <div
                                class="highlighted-text line-clamp-2 text-sm text-gray-700"
                                v-html="
                                  item._highlightResult?.field_wysiwyg?.value || item.field_wysiwyg
                                "
                              ></div>
                              <LinkSecondary
                                v-if="item.url"
                                type="blue"
                                class="mt-3 !text-xs"
                                :title="t('read-more')"
                                :link="removeCmsDomain(item.url)"
                              />
                            </div>
                          </NuxtLink>

                          <!-- Load more button -->
                          <div v-if="items.length > visibleHits" class="text-center">
                            <Button
                              :title="t('load-more')"
                              @click="visibleHits += 5"
                              type="outline"
                              class="w-full !text-sm"
                            />
                          </div>
                        </div>
                      </template>
                    </ais-hits>
                  </div>
                </transition>
              </div>

              <!-- No results UI -->
              <div v-else-if="state.query">
                <p class="mt-2 text-center">{{ $t('searchNotFound.title') }} "{{ state.query }}"</p>
              </div>
            </template>
          </ais-state-results>
        </div>
      </div>
    </ais-instant-search>
  </client-only>
</template>

<style>
.highlighted-text mark {
  background-color: #e0fbff;
  padding: 0 2px;
  border-radius: 3px;
}
</style>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
